/**
 * UV Transform Utilities
 * Drawing UV map canvas dengan texture transform visualization
 */

/**
 * Draw UV Map to canvas
 * Shows UV wireframe dan texture dengan transform handles
 */
export function drawUvMapCanvas(
  canvasRef,
  ctxRef,
  selectedMaterial,
  materialUvTransformMode,
  displaySize = 300
) {
  if (
    !canvasRef ||
    !ctxRef ||
    !selectedMaterial?.material?.map
  )
    return

  const canvas = canvasRef
  const ctx = ctxRef
  const texture = selectedMaterial.material.map

  // Clear canvas
  ctx.clearRect(0, 0, displaySize, displaySize)

  // Draw checkerboard background
  const checkSize = 20
  ctx.fillStyle = '#2a2a2a'
  ctx.fillRect(0, 0, displaySize, displaySize)

  for (let y = 0; y < displaySize; y += checkSize) {
    for (let x = 0; x < displaySize; x += checkSize) {
      if ((x / checkSize + y / checkSize) % 2 === 0) {
        ctx.fillStyle = '#1a1a1a'
        ctx.fillRect(x, y, checkSize, checkSize)
      }
    }
  }

  const canvasSize = displaySize
  const centerX = displaySize / 2
  const centerY = displaySize / 2

  // ========================================
  // STEP 1: Draw UV Wireframe (FIXED - doesn't move)
  // ========================================
  if (selectedMaterial?.mesh?.geometry) {
    const geometry = selectedMaterial.mesh.geometry
    const uvAttribute = geometry.attributes.uv
    const indexAttribute = geometry.index

    if (uvAttribute) {
      ctx.save()
      ctx.strokeStyle = 'rgba(100, 150, 255, 0.4)'
      ctx.lineWidth = 1
      ctx.setLineDash([])

      if (indexAttribute) {
        const indices = indexAttribute.array
        const uvs = uvAttribute.array

        for (let i = 0; i < indices.length; i += 3) {
          const i1 = indices[i] * 2
          const i2 = indices[i + 1] * 2
          const i3 = indices[i + 2] * 2

          const u1 = uvs[i1]
          const v1 = uvs[i1 + 1]
          const u2 = uvs[i2]
          const v2 = uvs[i2 + 1]
          const u3 = uvs[i3]
          const v3 = uvs[i3 + 1]

          const x1 = u1 * canvasSize
          const y1 = v1 * canvasSize
          const x2 = u2 * canvasSize
          const y2 = v2 * canvasSize
          const x3 = u3 * canvasSize
          const y3 = v3 * canvasSize

          ctx.beginPath()
          ctx.moveTo(x1, y1)
          ctx.lineTo(x2, y2)
          ctx.lineTo(x3, y3)
          ctx.closePath()
          ctx.stroke()
        }
      }

      ctx.restore()
    }
  }

  // ========================================
  // STEP 2: Draw Texture (MOVABLE - can be transformed)
  // ========================================
  if (texture.image) {
    const img = texture.image
    const aspect = img.width / img.height

    // Read values directly from texture
    const currentRepeatX = texture.repeat.x
    const currentRepeatY = texture.repeat.y
    const currentOffsetX = texture.offset.x
    const currentOffsetY = texture.offset.y
    const currentRotation = texture.rotation

    // Calculate size based on repeat
    let texWidth = canvasSize * 0.6 * Math.abs(currentRepeatX)
    let texHeight = canvasSize * 0.6 * Math.abs(currentRepeatY)

    // Maintain aspect ratio
    if (texWidth / texHeight > aspect) {
      texWidth = texHeight * aspect
    } else {
      texHeight = texWidth / aspect
    }

    // Apply rotation
    ctx.save()
    ctx.translate(centerX, centerY)
    const displayRotation = -currentRotation + Math.PI
    ctx.rotate(displayRotation)

    // Apply offset (in canvas coordinates)
    const offsetX = -(currentOffsetX * canvasSize * 0.5)
    const offsetY = -(currentOffsetY * canvasSize * 0.5)

    // Draw texture
    ctx.globalAlpha = 0.9
    ctx.drawImage(
      img,
      -texWidth / 2 + offsetX,
      -texHeight / 2 + offsetY,
      texWidth,
      texHeight
    )

    // Draw bounding box
    ctx.strokeStyle = '#10b981'
    ctx.lineWidth = 2
    ctx.setLineDash([])
    ctx.strokeRect(
      -texWidth / 2 + offsetX,
      -texHeight / 2 + offsetY,
      texWidth,
      texHeight
    )

    // Draw handles based on mode
    const handleSize = 8
    ctx.fillStyle = '#10b981'
    ctx.strokeStyle = '#fff'
    ctx.lineWidth = 2

    if (materialUvTransformMode === 'scale') {
      // Corner handles
      const corners = [
        [-texWidth / 2 + offsetX, -texHeight / 2 + offsetY],
        [texWidth / 2 + offsetX, -texHeight / 2 + offsetY],
        [texWidth / 2 + offsetX, texHeight / 2 + offsetY],
        [-texWidth / 2 + offsetX, texHeight / 2 + offsetY],
      ]

      corners.forEach(([x, y]) => {
        ctx.beginPath()
        ctx.arc(x, y, handleSize, 0, Math.PI * 2)
        ctx.fill()
        ctx.stroke()
      })
    } else if (materialUvTransformMode === 'rotate') {
      // Rotation handle (top center)
      const handleX = offsetX
      const handleY = -texHeight / 2 + offsetY - 30
      ctx.beginPath()
      ctx.arc(handleX, handleY, handleSize, 0, Math.PI * 2)
      ctx.fill()
      ctx.stroke()

      // Rotation line
      ctx.strokeStyle = '#10b981'
      ctx.lineWidth = 1
      ctx.setLineDash([5, 5])
      ctx.beginPath()
      ctx.moveTo(offsetX, -texHeight / 2 + offsetY)
      ctx.lineTo(handleX, handleY)
      ctx.stroke()
    }

    ctx.restore()
  }
}

/**
 * Download UV map as PNG (solid white faces on transparent background)
 * Untuk Photoshop selection
 */
export function downloadUvMap(selectedMaterial) {
  if (!selectedMaterial?.mesh?.geometry) {
    alert('No geometry selected')
    return
  }

  const geometry = selectedMaterial.mesh.geometry
  const uvAttribute = geometry.attributes.uv
  const indexAttribute = geometry.index

  if (!uvAttribute) {
    alert('No UV coordinates found on this mesh')
    return
  }

  // Create a high-resolution canvas for export
  const exportSize = 2048
  const exportCanvas = document.createElement('canvas')
  exportCanvas.width = exportSize
  exportCanvas.height = exportSize
  const ctx = exportCanvas.getContext('2d')

  // Transparent background
  ctx.clearRect(0, 0, exportSize, exportSize)

  // Draw UV faces as solid fills only (no outlines)
  ctx.fillStyle = '#FFFFFF'

  if (indexAttribute) {
    const indices = indexAttribute.array
    const uvs = uvAttribute.array

    for (let i = 0; i < indices.length; i += 3) {
      const i1 = indices[i] * 2
      const i2 = indices[i + 1] * 2
      const i3 = indices[i + 2] * 2

      const u1 = uvs[i1]
      const v1 = uvs[i1 + 1]
      const u2 = uvs[i2]
      const v2 = uvs[i2 + 1]
      const u3 = uvs[i3]
      const v3 = uvs[i3 + 1]

      const x1 = u1 * exportSize
      const y1 = v1 * exportSize
      const x2 = u2 * exportSize
      const y2 = v2 * exportSize
      const x3 = u3 * exportSize
      const y3 = v3 * exportSize

      ctx.beginPath()
      ctx.moveTo(x1, y1)
      ctx.lineTo(x2, y2)
      ctx.lineTo(x3, y3)
      ctx.closePath()
      ctx.fill()
    }
  } else {
    const uvs = uvAttribute.array

    for (let i = 0; i < uvs.length; i += 6) {
      const u1 = uvs[i]
      const v1 = uvs[i + 1]
      const u2 = uvs[i + 2]
      const v2 = uvs[i + 3]
      const u3 = uvs[i + 4]
      const v3 = uvs[i + 5]

      const x1 = u1 * exportSize
      const y1 = v1 * exportSize
      const x2 = u2 * exportSize
      const y2 = v2 * exportSize
      const x3 = u3 * exportSize
      const y3 = v3 * exportSize

      ctx.beginPath()
      ctx.moveTo(x1, y1)
      ctx.lineTo(x2, y2)
      ctx.lineTo(x3, y3)
      ctx.closePath()
      ctx.fill()
    }
  }

  // Download the canvas as PNG
  exportCanvas.toBlob((blob) => {
    if (blob) {
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `uv-map-${selectedMaterial.meshName || 'mesh'}-${Date.now()}.png`
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      URL.revokeObjectURL(url)
    }
  }, 'image/png')
}

